import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ReplyToTicketRequest, TicketData, TicketMessageData } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";

const STATUS_OPTIONS = [
  { label: "باز", value: "open" },
  { label: "در حال بررسی", value: "in_progress" },
  { label: "پاسخ داده شده", value: "answered" },
  { label: "بسته", value: "closed" },
];

function getStatusLabel(status?: string) {
  const normalized = (status || "").toLowerCase();
  if (normalized === "open" || normalized === "pending") return "باز";
  if (normalized === "in_progress") return "در حال بررسی";
  if (normalized === "answered") return "پاسخ داده شده";
  if (normalized === "closed") return "بسته";
  return status || "-";
}

function getPriorityLabel(priority?: string) {
  const normalized = (priority || "").toLowerCase();
  if (normalized === "low") return "کم";
  if (normalized === "medium") return "متوسط";
  if (normalized === "high") return "بالا";
  if (normalized === "critical" || normalized === "urgent") return "فوری";
  return priority || "-";
}

export default function TicketReply() {
  const { id } = useParams();
  const ticketId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { setMessage } = useContext(AppContext);
  const ticket = (location.state as { ticket?: TicketData } | null)?.ticket;

  const [messages, setMessages] = useState<TicketMessageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    content: "",
    status: "answered",
  });

  const isFormValid = useMemo(() => Boolean(form.content.trim() && form.status.trim()), [form]);

  useEffect(() => {
    if (!ticketId) {
      setMessage({ type: "error", message: "شناسه تیکت معتبر نیست." });
      navigate("/admin/tickets");
      return;
    }

    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await appRepo.getTicketMessages(ticketId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت پیام‌های تیکت ناموفق بود." });
          navigate("/admin/tickets");
          return;
        }
        setMessages(response.data.reverse());
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [navigate, setMessage, ticketId]);

  const onSubmitReply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid || !ticketId) {
      setMessage({ type: "error", message: "متن پاسخ و وضعیت را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: ReplyToTicketRequest = {
        content: form.content.trim(),
        status: form.status,
      };

      const response = await appRepo.replyToTicket(ticketId, payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ارسال پاسخ با خطا مواجه شد." });
        return;
      }

      const newReply: TicketMessageData = {
        id: (response.data as TicketMessageData | null)?.id || Date.now(),
        is_admin: true,
        content: form.content.trim(),
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, newReply]);
      setForm((prev) => ({ ...prev, content: "" }));
      setMessage({ type: "success", message: "پاسخ با موفقیت ثبت شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title={`پاسخ به تیکت #${ticketId || "-"}`} />

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-300">گفتگو با کاربر</p>
        <Link
          to="/admin/tickets"
          className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-xs text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
        >
          بازگشت به لیست تیکت‌ها
        </Link>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
          <p className="text-xs text-slate-400">عنوان تیکت</p>
          <p className="mt-1 text-sm text-slate-100">{ticket?.title || "-"}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
          <p className="text-xs text-slate-400">نام کاربر</p>
          <p className="mt-1 text-sm text-slate-100">{ticket?.username || "-"}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
          <p className="text-xs text-slate-400">وضعیت فعلی</p>
          <p className="mt-1 text-sm text-slate-100">{getStatusLabel(ticket?.status)}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
          <p className="text-xs text-slate-400">اولویت</p>
          <p className="mt-1 text-sm text-slate-100">{getPriorityLabel(ticket?.priority)}</p>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت پیام‌ها...
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900/70 to-slate-950/60 p-4 sm:p-5">
          {messages.length ? (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.is_admin ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`w-full max-w-[92%] rounded-2xl border p-4 shadow-sm sm:max-w-[78%] ${
                      message.is_admin
                        ? "border-indigo-400/30 bg-indigo-500/15"
                        : "border-slate-700 bg-slate-900/80"
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                          message.is_admin
                            ? "bg-indigo-500/20 text-indigo-200"
                            : "bg-slate-700/70 text-slate-300"
                        }`}
                      >
                        {message.is_admin ? "پاسخ مدیر" : "پیام کاربر"}
                      </span>
                      <span className="text-[11px] text-slate-400 ltr">{message.created_at}</span>
                    </div>
                    <p className="whitespace-pre-wrap leading-7 text-sm text-slate-100">{message.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
              هنوز پیامی برای این تیکت ثبت نشده است.
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmitReply} className="grid gap-4">
          <label className="space-y-2">
            <span className="text-sm text-slate-300">وضعیت تیکت پس از پاسخ</span>
            <select
              value={form.status}
              onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">متن پاسخ مدیر</span>
            <textarea
              rows={5}
              value={form.content}
              onChange={(event) => setForm((prev) => ({ ...prev, content: event.target.value }))}
              placeholder="پاسخ خود را اینجا بنویسید..."
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
            />
          </label>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ارسال..." : "ارسال پاسخ"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
